// PRC-017 · A5: pedir el enlace para crear una contraseña nueva (/recuperar-contrasena).
import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import { SimpleLayout } from '../components/JourneyLayouts';
import { JourneyField, Notice, StateIcon } from '../components/JourneyBlocks';
import { Eyebrow, JourneyButton, JourneyCard, JourneyText } from '../components/JourneyBasics';
import { J } from '../journeyTokens';
import { EMAIL_RE, OFFLINE_MESSAGE, RESEND_WAIT_SECONDS, requestPasswordReset } from '../journeyAuth';

const RecoverPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  // PRC-030: error del campo, validado al salir de él y al enviar.
  const [emailError, setEmailError] = useState<string | null>(null);
  const [wait, setWait] = useState(0);

  useEffect(() => {
    if (wait <= 0) return;
    const t = window.setTimeout(() => setWait((w) => w - 1), 1000);
    return () => window.clearTimeout(t);
  }, [wait]);

  const send = async (event?: React.FormEvent) => {
    event?.preventDefault();
    setError(null);
    const fieldError = !email.trim() ? 'Escribe el email de tu cuenta.' : !EMAIL_RE.test(email.trim()) ? 'Este email no parece correcto.' : null;
    setEmailError(fieldError);
    if (fieldError) { document.getElementById('a5-email')?.focus(); return; }
    setBusy(true);
    const r = await requestPasswordReset(email.trim());
    setBusy(false);
    if (r === 'sent' || r === 'limited') {
      setSent(true);
      setWait(RESEND_WAIT_SECONDS);
    } else {
      setError(r === 'offline' ? OFFLINE_MESSAGE : 'No hemos podido enviar el enlace. Inténtalo en unos minutos.');
    }
  };

  const back = <Box component="a" href="/login" sx={{ color: J.skyText, fontWeight: 600, textDecoration: 'none', fontSize: 14 }}>Volver a iniciar sesión</Box>;

  return (
    <SimpleLayout narrow>
      <Eyebrow tone="sky">Acceso</Eyebrow>
      <JourneyCard sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {!sent ? (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <JourneyText variant="title">¿Has olvidado la contraseña?</JourneyText>
              <JourneyText variant="lead">Escribe el email de tu cuenta y te enviaremos un enlace para crear una nueva.</JourneyText>
            </Box>
            <Box component="form" noValidate onSubmit={send} sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <JourneyField
                id="a5-email" label="Email" type="email" value={email} autoComplete="email" autoFocus error={emailError}
                onChange={(e) => { setEmail(e.target.value); setEmailError(null); }}
                onBlur={() => setEmailError(email.trim() && !EMAIL_RE.test(email.trim()) ? 'Este email no parece correcto.' : null)}
              />
              {error && <Notice tone="danger">{error}</Notice>}
              <JourneyButton type="submit" block disabled={busy}>{busy ? 'Enviando…' : 'Enviar enlace'}</JourneyButton>
            </Box>
            {back}
          </>
        ) : (
          <>
            <StateIcon tone="info"><MarkEmailReadOutlinedIcon /></StateIcon>
            <JourneyText variant="title">Revisa tu correo</JourneyText>
            <JourneyText variant="lead">
              Si <b style={{ color: J.text }}>{email.trim()}</b> tiene una cuenta, recibirás un enlace para crear una contraseña nueva.
            </JourneyText>
            {error && <Notice tone="danger">{error}</Notice>}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
              <JourneyButton kind="ghost" disabled={wait > 0 || busy} onClick={() => send()}>{wait > 0 ? `Reenviar enlace (${wait} s)` : 'Reenviar enlace'}</JourneyButton>
              {back}
            </Box>
          </>
        )}
      </JourneyCard>
    </SimpleLayout>
  );
};

export default RecoverPasswordPage;
